import { useRef, useState } from 'react'
import { Button } from '../../components/ui/button'
import { useT } from '../../i18n'
import type { ImageRectangle } from '../../lib/problem-reporting/images'
import { reportCopy } from './copy'
import type { SubmissionImage } from './attachments'
import type { Submission } from './ReportImages'
export default function ImageEditor({
  image,
  index,
  controller,
  editable,
  approved,
  onApproval,
}: {
  image: Readonly<SubmissionImage>
  index: number
  controller: Submission
  editable: boolean
  approved: boolean
  onApproval: (url: string, checked: boolean) => void
}) {
  const t = useT(reportCopy)
  const bitmap = useRef<HTMLImageElement>(null)
  const [rectangle, setRectangle] = useState<ImageRectangle>({
    x: 0,
    y: 0,
    width: 50,
    height: 50,
  })
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })
  const start = useRef<{ x: number; y: number } | null>(null)
  const point = (event: React.PointerEvent) => {
    const element = bitmap.current!
    const bounds = element.getBoundingClientRect()
    return {
      x: Math.round(
        (Math.max(0, Math.min(bounds.width, event.clientX - bounds.left)) *
          element.naturalWidth) /
          bounds.width,
      ),
      y: Math.round(
        (Math.max(0, Math.min(bounds.height, event.clientY - bounds.top)) *
          element.naturalHeight) /
          bounds.height,
      ),
    }
  }
  return (
    <div className="space-y-2 rounded-[var(--radius-control)] border border-[var(--color-border)] p-3">
      <p className="text-xs" role="status">
        {t.image} {index + 1} · {t[image.status]}
      </p>
      {image.previewUrl && (
        <>
          <div
            className="relative"
            style={{ touchAction: editable ? 'none' : 'auto' }}
            onPointerDown={(event) => {
              if (!editable) return
              start.current = point(event)
              event.currentTarget.setPointerCapture(event.pointerId)
            }}
            onPointerMove={(event) => {
              if (!start.current || !editable) return
              const end = point(event)
              setRectangle({
                x: Math.min(start.current.x, end.x),
                y: Math.min(start.current.y, end.y),
                width: Math.abs(end.x - start.current.x),
                height: Math.abs(end.y - start.current.y),
              })
            }}
            onPointerUp={() => {
              start.current = null
            }}
            onPointerCancel={() => {
              start.current = null
            }}
          >
            <img
              ref={bitmap}
              src={image.previewUrl}
              alt={`${t.image} ${index + 1}`}
              draggable={false}
              onLoad={(event) =>
                setDimensions({
                  width: event.currentTarget.naturalWidth,
                  height: event.currentTarget.naturalHeight,
                })
              }
              className="block h-auto w-full"
            />
            {editable && dimensions.width ? (
              <div
                aria-hidden
                className="pointer-events-none absolute border-2 border-dashed border-[var(--color-text)] bg-[var(--color-surface-muted)]/50"
                style={{
                  left: `${(100 * rectangle.x) / dimensions.width}%`,
                  top: `${(100 * rectangle.y) / dimensions.height}%`,
                  width: `${(100 * rectangle.width) / dimensions.width}%`,
                  height: `${(100 * rectangle.height) / dimensions.height}%`,
                }}
              />
            ) : null}
          </div>
          {editable && (
            <>
              <p className="text-xs">{t.rectangle}</p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {(['x', 'y', 'width', 'height'] as const).map((field) => (
                  <label key={field} className="text-xs">
                    {t[field]}
                    <input
                      type="number"
                      min={0}
                      value={rectangle[field]}
                      onChange={(event) =>
                        setRectangle({
                          ...rectangle,
                          [field]: Math.max(0, Number(event.target.value)),
                        })
                      }
                      className="mt-1 w-full rounded border border-[var(--color-border)] bg-[var(--color-surface)] p-1"
                    />
                  </label>
                ))}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!rectangle.width || !rectangle.height}
                  onClick={() =>
                    void controller.redactImage(image.key, [rectangle])
                  }
                >
                  {t.redact}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => controller.removeImage(image.key)}
                >
                  {t.remove}
                </Button>
              </div>
              <label className="flex items-start gap-2 text-xs leading-5">
                <input
                  type="checkbox"
                  checked={approved}
                  onChange={(event) =>
                    onApproval(image.previewUrl!, event.target.checked)
                  }
                  className="mt-1"
                />
                {t.review}
              </label>
            </>
          )}
        </>
      )}
      {image.status === 'failed' && (
        <div className="text-xs">
          {image.confirmation === 'rejected' ? (
            t.rejected
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={() => void controller.retryImage(image.key)}
            >
              {t.retry}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
